import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { InquiryHero, SuccessBanner, PrivacyNote, FormActions, submitInquiry } from './formUtils';
import { Section, TextField, TextareaField, SelectField, MultiSelectField, FileUploadField, ConditionalText, ConsentSection } from './FormFields';
import { HOW_HEARD_OPTIONS, COUNTRY_LIST } from '../../data/partnerOptions';
import { COSMETICS_OPTIONS, findActive } from '../../data/cosmeticActives';

const O = COSMETICS_OPTIONS;

const initial = {
  fullName: '', company: '', jobTitle: '', email: '', phone: '', countryIncorporation: '',
  companyType: '', companyTypeOther: '',
  serviceScope: [],
  ingredientCategory: '', ingredientCategoryOther: '', catalogueItem: '', ingredientName: '', structureAvailable: '',
  targetSpec: '', gradeRequired: '',
  sampleQuantity: '', commercialQuantity: '', timeline: '',
  markets: [], documents: [], existingSupplier: '',
  additionalDetails: '', howHeard: '',
  consentRequired: false, consentOptional: false,
};

const param = (k) => { try { return new URLSearchParams(window.location.search).get(k) || ''; } catch (e) { return ''; } };
const cdaRef = () => param('cda');
const preselected = () => {
  const a = findActive(param('item'));
  if (!a) return {};
  const cat = { uv: 'UV filter', scalp: 'Scalp and hair care', brightening: 'Brightening / even tone', preservation: 'Preservation and antioxidants' }[a.category] || '';
  return { catalogueItem: `${a.inci} (CAS ${a.cas})`, ingredientName: `${a.inci} / CAS ${a.cas}`, ingredientCategory: cat, structureAvailable: 'It is a known molecule (INCI / CAS given)' };
};

export const CosmeticsActivesForm = () => {
  const [data, setData] = useState(() => ({ ...initial, ...preselected() }));
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showConsentError, setShowConsentError] = useState(false);
  const cda = cdaRef();
  const selected = findActive(param('item'));

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!data.consentRequired) { setShowConsentError(true); return; }
    setError('');
    setSubmitting(true);
    try {
      const sections = [
        { title: 'Contact Information', fields: [
          { label: 'Full name', value: data.fullName },
          { label: 'Company / organization', value: data.company },
          { label: 'Job title / role', value: data.jobTitle },
          { label: 'Business email', value: data.email },
          { label: 'Phone number', value: data.phone },
          { label: 'Country of incorporation', value: data.countryIncorporation },
          { label: 'Company type', value: data.companyType === 'Other (please specify)' ? data.companyTypeOther : data.companyType },
        ]},
        { title: 'Scope of Request (CRDMO + CDMO)', fields: [
          { label: 'Services needed', value: data.serviceScope.join(', ') },
          { label: 'Existing supplier situation', value: data.existingSupplier },
          { label: 'CDA reference', value: cda ? `LAMKO-CDA-${cda}` : 'None yet' },
        ]},
        { title: 'Ingredient', fields: [
          { label: 'Ingredient category', value: data.ingredientCategory === 'Other (please specify)' ? data.ingredientCategoryOther : data.ingredientCategory },
          { label: 'From LAMKO catalogue', value: data.catalogueItem },
          { label: 'Ingredient (INCI / chemical name / CAS)', value: data.ingredientName },
          { label: 'Structure available?', value: data.structureAvailable },
          { label: 'Target specification', value: data.targetSpec },
          { label: 'Grade required', value: data.gradeRequired },
        ]},
        { title: 'Quantities and Timeline', fields: [
          { label: 'Sample quantity', value: data.sampleQuantity },
          { label: 'Expected commercial quantity', value: data.commercialQuantity },
          { label: 'Timeline', value: data.timeline },
        ]},
        { title: 'Regulatory and Documentation', fields: [
          { label: 'Target markets', value: data.markets.join(', ') },
          { label: 'Documents needed', value: data.documents.join(', ') },
        ]},
        { title: 'Additional Context', fields: [
          { label: 'Additional details', value: data.additionalDetails },
          { label: 'How did you hear about LAMKO?', value: data.howHeard },
          { label: 'Marketing consent', value: data.consentOptional ? 'Yes' : 'No' },
        ]},
      ];
      await submitInquiry({
        formType: 'cosmetic-actives',
        sections,
        meta: { fullName: data.fullName, companyName: data.company, businessEmail: data.email, country: data.countryIncorporation },
        files,
      });
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again or email info@lamko.co.kr.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setData(initial); setFiles([]); setError(''); setSuccess(false); };

  return (
    <div className="min-h-screen bg-gray-50">
      <InquiryHero
        title="Cosmetic Actives (CRDMO + CDMO)"
        subtitle="One form for research, development, scale-up and supply of cosmetic active ingredients"
      />
      <section className="py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {success && <SuccessBanner />}
            {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm" data-testid="error-banner">{error}</div>}

            <div className="mb-8 grid md:grid-cols-2 gap-4" data-testid="form-intro">
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
                {selected ? (
                  <>
                    <div className="text-xs font-bold tracking-widest text-teal-700 uppercase">Selected active</div>
                    <div className="text-lg font-semibold text-gray-900 mt-1">{selected.inci}</div>
                    <div className="text-xs text-gray-500">{selected.short}</div>
                    <dl className="mt-3 grid grid-cols-[110px_1fr] gap-y-1 text-sm">
                      <dt className="text-gray-500">CAS</dt><dd className="text-gray-900 font-medium">{selected.cas}</dd>
                      <dt className="text-gray-500">Formula</dt><dd className="text-gray-900">{selected.formula}</dd>
                      <dt className="text-gray-500">MW</dt><dd className="text-gray-900">{selected.mw}</dd>
                      <dt className="text-gray-500">Appearance</dt><dd className="text-gray-900">{selected.appearance}</dd>
                      <dt className="text-gray-500">Function</dt><dd className="text-gray-900">{selected.function}</dd>
                    </dl>
                    <div className="mt-3 flex flex-wrap gap-3 text-sm">
                      <Link to={`/products/cosmetic-actives/${selected.slug}`} className="font-semibold text-teal-700 underline underline-offset-2">Full specification</Link>
                      <Link to="/products/cosmetic-actives" className="font-semibold text-teal-700 underline underline-offset-2">Choose a different active</Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-xs font-bold tracking-widest text-teal-700 uppercase">Product</div>
                    <div className="text-lg font-semibold text-gray-900 mt-1">Which active is this about?</div>
                    <p className="text-sm text-gray-700 mt-2 leading-relaxed">Pick one from the LAMKO cosmetic actives line and it is filled in below, or describe a custom molecule in the Ingredient section.</p>
                    <Link to="/products/cosmetic-actives" className="inline-flex mt-3 items-center rounded-md bg-teal-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-teal-800">Browse the product line</Link>
                  </>
                )}
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 leading-relaxed">
                <p><b>Why one form:</b> cosmetic-active projects usually move from a few grams of sample to kilogram pilot batches and then to tonne-scale supply. Tell us where you are today and where you want to be, and the same LAMKO project manager follows the project through CRDMO and CDMO stages.</p>
                <p className="mt-2">
                  {cda
                    ? <span className="text-teal-800">CDA <b>LAMKO-CDA-{cda}</b> is linked to this enquiry, so you can include structures and specifications.</span>
                    : <>No CDA in place yet? Share only what you are comfortable with here, or <Link to="/cda?from=/partner/cosmetic-actives" className="font-semibold text-teal-700 underline underline-offset-2">request our mutual CDA first</Link>.</>}
                </p>
                <p className="mt-2 text-xs text-gray-500">Confidential by default. Seen only by your assigned project manager. Draft saves as you go; colleagues can be invited to fill their parts.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <Section title="Contact Information">
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField name="fullName" label="Full name" required helper="Your first and last name as you'd like to be addressed." placeholder="e.g., Dr. Min-Jun Park" value={data.fullName} onChange={set('fullName')} />
                  <TextField name="company" label="Company / organization" required helper="Legal entity or institution name." placeholder="e.g., ABC Cosmetics Co., Ltd." value={data.company} onChange={set('company')} />
                  <TextField name="jobTitle" label="Job title / role" required helper="Your current position or functional role." placeholder="e.g., R&D Manager, Purchasing" value={data.jobTitle} onChange={set('jobTitle')} />
                  <TextField name="email" type="email" label="Business email" required helper="We'll use this email for all project correspondence." placeholder="e.g., mjpark@company.com" value={data.email} onChange={set('email')} />
                  <TextField name="phone" type="tel" label="Phone number" helper="Include country code. Used only for urgent follow-ups." placeholder="e.g., +82-10-1234-5678" value={data.phone} onChange={set('phone')} />
                  <SelectField name="countryIncorporation" label="Country of incorporation" required helper="Where is your company legally registered?" options={COUNTRY_LIST} value={data.countryIncorporation} onChange={set('countryIncorporation')} />
                </div>
                <SelectField name="companyType" label="Company type" required helper="Helps us route the enquiry to the right team." options={O.companyType} value={data.companyType} onChange={set('companyType')} />
                <ConditionalText show={data.companyType === 'Other (please specify)'} name="companyTypeOther" placeholder="Please describe your company" value={data.companyTypeOther} onChange={set('companyTypeOther')} />
              </Section>

              <Section title="What do you need?">
                <MultiSelectField name="serviceScope" label="Services (select all that apply)" required helper="Research (CRDMO), development and manufacturing (CDMO) or supply. Mixed selections are normal." options={O.serviceScope} values={data.serviceScope} onChange={set('serviceScope')} columns={1} />
                <SelectField name="existingSupplier" label="Existing supplier situation" helper="Second source, switch, or a new project?" options={O.existingSupplier} value={data.existingSupplier} onChange={set('existingSupplier')} />
              </Section>

              <Section title="Ingredient">
                <SelectField name="ingredientCategory" label="Ingredient category" required helper="Primary function of the active." options={O.ingredientCategory} value={data.ingredientCategory} onChange={set('ingredientCategory')} />
                <ConditionalText show={data.ingredientCategory === 'Other (please specify)'} name="ingredientCategoryOther" placeholder="Please describe the ingredient category" value={data.ingredientCategoryOther} onChange={set('ingredientCategoryOther')} />
                <SelectField name="catalogueItem" label="Is it one of our listed actives?" helper="Pick from the LAMKO cosmetic actives line, or choose custom." options={O.catalogueItem} value={data.catalogueItem} onChange={set('catalogueItem')} />
                <TextField name="ingredientName" label="Ingredient (INCI name, chemical name or CAS number)" required helper="For custom molecules, a known name or CAS is enough at this stage; structures can follow after the CDA." placeholder="e.g., Piroctone Olamine / CAS 68890-66-4" value={data.ingredientName} onChange={set('ingredientName')} />
                <SelectField name="structureAvailable" label="Chemical structure available?" helper="For custom or derivative molecules." options={O.structureAvailable} value={data.structureAvailable} onChange={set('structureAvailable')} />
                <TextField name="targetSpec" label="Target specification" helper="Purity, assay, key impurity limits, colour, particle size, or say 'standard cosmetic grade'." placeholder="e.g., HPLC purity ≥ 99.0 %, APHA ≤ 50" value={data.targetSpec} onChange={set('targetSpec')} />
                <SelectField name="gradeRequired" label="Grade and certification" helper="Halal, vegan or GMP-style documentation can be arranged." options={O.gradeRequired} value={data.gradeRequired} onChange={set('gradeRequired')} />
              </Section>

              <Section title="Quantities and Timeline">
                <div className="grid sm:grid-cols-2 gap-5">
                  <SelectField name="sampleQuantity" label="Sample quantity (first step)" helper="For lab evaluation and formulation trials." options={O.sampleQuantity} value={data.sampleQuantity} onChange={set('sampleQuantity')} />
                  <SelectField name="commercialQuantity" label="Expected commercial quantity" required helper="Annual volume once the project is running. A rough range is fine." options={O.commercialQuantity} value={data.commercialQuantity} onChange={set('commercialQuantity')} />
                </div>
                <SelectField name="timeline" label="Timeline" required helper="When do you need samples or first deliveries?" options={O.timeline} value={data.timeline} onChange={set('timeline')} />
              </Section>

              <Section title="Regulatory and Documentation">
                <MultiSelectField name="markets" label="Target markets" helper="Determines which listings and limits apply." options={O.markets} values={data.markets} onChange={set('markets')} />
                <MultiSelectField name="documents" label="Documents you will need" helper="We prepare the dossier alongside the samples." options={O.documents} values={data.documents} onChange={set('documents')} />
              </Section>

              <Section title="Additional Context and Documents">
                <TextareaField name="additionalDetails" label="Additional details" helper="Formulation type (rinse-off, leave-on, sun care), incompatibilities, target use level, benchmarks, or anything else useful." placeholder="Tell us about the project..." value={data.additionalDetails} onChange={set('additionalDetails')} />
                <FileUploadField name="files" label="Upload specifications or briefs" helper="Accepted formats: PDF, DOC, DOCX, XLS, XLSX, CDX, MOL, SDF (max 25 MB per file). Please share structures only after the CDA." files={files} onChange={setFiles} />
                <SelectField name="howHeard" label="How did you hear about LAMKO?" helper="This helps us understand our reach." options={HOW_HEARD_OPTIONS} value={data.howHeard} onChange={set('howHeard')} />
              </Section>

              <FormActions onReset={reset} isSubmitting={submitting} />
              <ConsentSection variant="customer" consent1={data.consentRequired} consent2={data.consentOptional} onChange1={set('consentRequired')} onChange2={set('consentOptional')} showError={showConsentError} />
              <PrivacyNote />
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CosmeticsActivesForm;
